<template>
  <Loader :value="promises.fetchOne" :message="$metaInfo.title" class="lead-mutate">
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumbs">
        <template #right>
          <TaskCreateButton :reference="{ onModel: 'Lead', ref: id }" class="mr-2" />
          <TaskButtonList v-model="item.tasks" class="mr-2" :reference-id="id" />

          <Actions placement="lead" :query="{ lead: id }">
            <template #bottom>
              <ButtonComponent
                :icon="['fal', 'sync-alt']"
                :label="$t('syncUser')"
                :promise="promises.fetchOne"
                @click="fetch({ id })"
              />
              <ButtonComponent
                tag="router-link"
                :to="{ name: 'contact-edit', params: { id: $path('contact.id', item) } }"
                :label="$t('editContact')"
                :icon="['fal', 'pen']"
              />
              <ButtonComponent
                v-if="$store.getters.hasAccess('admin')"
                :label="$t('delete')"
                @click="removeDialog = true"
                :icon="['fal', 'times']"
              />
            </template>
          </Actions>
        </template>
      </HeaderComponent>
      <div class="lead-mutate__content">
        <Comments :value="item.comments" @submit="comment" :promise="commentPromise" />
        <ContactCard :value="item.contact" />
        <Contracts :value="$path('contact.contracts', item)">
          <template #after-title>
            <router-link
              v-if="item.id"
              :to="{
                name: 'custom-contract-create',
                query: { contact: $path('contact.id', item), returnTo: $route.path },
              }"
            >
              {{ $t('createContract') }}
            </router-link>
          </template>
        </Contracts>
        <FileList :value="item.comments" />
        <Card v-if="onModel === 'project'" tag="form" @submit.prevent="submit">
          <template #default>
            <InputComponent :label="$t('leadReferences')" data-field="references">
              <template #default>
                <ChecklistComponent
                  v-if="item.reference"
                  v-model="data.scopes"
                  :items="fetchResidentials"
                  :compare="(a, b) => a.ref === b.ref"
                  class="elder-input__element"
                >
                  <template #default>
                    <CheckboxComponent
                      v-model="data.scopes"
                      :items="fetchResidentials"
                      :state="{ ref: item.reference.id, onModel: item.reference.onModel }"
                      :compare="(a, b) => a.ref === b.ref"
                      :label="$tc('theWholeProject', 1)"
                    />
                    <hr />
                  </template>
                </ChecklistComponent>
              </template>
            </InputComponent>
          </template>
          <template #footer>
            <ButtonComponent
              :label="$t('save')"
              theme="primary"
              type="submit"
              :icon="['fal', 'arrow-to-bottom']"
              :promise="promises.mutate"
            />
          </template>
        </Card>
      </div>
      <RemoveComponent
        :show.sync="removeDialog"
        :title="$metaInfo.title"
        :action="() => remove({ id })"
        @onSuccess="
          $router.push({
            name: `${onModel}-dashboard`,
            params: { id: $path('reference.id', item) },
          })
        "
        :resource="$tc('lead')"
      />
    </div>
  </Loader>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import { mapi18n } from '@/i18n'
import Model from '@/models/Lead/Mutate'
import ResidentialApi from '@/api/Residential'
import CommentApi from '@/api/Comment'

import { InputComponent } from 'vue-elder-input'
import { DropdownComponent } from 'vue-elder-dropdown'
import { CheckboxComponent } from 'vue-elder-checkbox'
import RemoveComponent from '@/components/Remove'
import ChecklistComponent from '@/components/Select/Checklist'
import ContactCard from '@/components/Contact/Card'
import Contracts from '@/components/Contract/Contracts'
import Comments from '@/components/Comment/List'
import HeaderComponent from '@/components/Header'
import FileList from '@/components/Comment/FileList'
import Card from '@/components/Card'
import TaskButtonList from '@/components/Task/ButtonList'
import TaskCreateButton from '@/components/Task/CreateButton'
import Actions from '@/components/Actions'

export default {
  props: {
    id: String,
  },
  data() {
    return {
      data: Model(),
      removeDialog: false,
      commentPromise: null,
      validator: {
        rules: {
          name: 'required',
          email: 'required|email',
          phone: 'mobile',
        },
        labels: 'labels',
        data: 'data',
      },
    }
  },
  watch: {
    id: {
      handler(id = '') {
        return this.fetch({ id })
      },
      immediate: true,
    },
    item: {
      handler(value) {
        this.data = Model(value)
        if (!value.isProcessed && value.id) this.mutate([{ id: this.id, isProcessed: true }, { setPromise: false }])
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState('Lead', ['item', 'promises']),
    ...mapi18n('labels', ['email', 'name', 'phone']),
    ...mapi18n('i18n', {
      loading() {
        return this.$tc('loading') + '...'
      },
      loadingLead() {
        return this.$t('loading', { resource: this.$tc('lead', 1) }) + '...'
      },
    }),
    isNew() {
      return !this.id
    },
    onModel() {
      return (this.$path('item.reference.onModel') || 'Project').toLowerCase()
    },
    breadcrumbs() {
      return [
        {
          title:
            (JSON.parse(sessionStorage.getItem(`${this.onModel}:category`)) || {}).label || this.$tc(this.onModel, 2),
          action: { name: `${this.onModel}s` },
        },
        {
          title: this.$path('item.reference.name'),
          action: {
            name: `${this.onModel}-dashboard`,
            params: { id: this.$path('item.reference.id') },
          },
        },
        {
          title: this.$tc('lead', 2),
          action: { name: `${this.onModel}-leads`, params: { id: this.$path('item.reference.id') } },
        },
        { title: this.$metaInfo.title },
      ]
    },
  },
  methods: {
    ...mapActions('Lead', ['fetch', 'mutate', 'remove']),
    ...mapMutations('Lead', ['set', 'addComment']),
    submit() {
      this.mutate(this.data)
    },

    comment(data) {
      this.commentPromise = CommentApi.mutate({
        reference: { onModel: 'Lead', ref: this.id },
        ...data,
      }).then(data => this.addComment(data))
    },
    fetchResidentials() {
      return ResidentialApi.get(
        { filter: { project: this.item.reference.id }, sort: { key: 'number', value: 1 } },
        { query: 'ResidentialSearch' },
      ).then(data =>
        data.map(e => ({
          value: { ref: e.id, onModel: 'Residential' },
          label: e.name,
        })),
      )
    },
  },
  metaInfo() {
    return {
      title: this.isNew ? this.$t('createLead') : this.$path('item.contact.name') || this.i18n.loadingLead,
    }
  },
  components: {
    InputComponent,
    HeaderComponent,
    DropdownComponent,
    CheckboxComponent,
    ChecklistComponent,
    ContactCard,
    Comments,
    RemoveComponent,
    Card,
    TaskButtonList,
    TaskCreateButton,
    FileList,
    Contracts,
    Actions,
  },
}
</script>

<style lang="scss">
.lead-mutate {
  background-color: $background-grey;

  &__content {
    display: grid;
    grid-gap: 2rem;
    grid-template-columns: 1fr minmax(max-content, 350px);
    grid-template-rows: auto auto 1fr;
    align-items: flex-start;

    .comments {
      grid-row-end: span 4;
    }
  }

  &__form {
    margin: 0 auto;
    max-width: 550px;
  }

  &__inputs {
    display: grid;
    align-items: center;
    grid-template-columns: 1fr;
    grid-gap: $form-gap-row / 2 $form-gap-column;
    align-items: flex-start;
  }

  [type='submit'] {
    margin-right: auto;
  }

  [data-field='references'] {
    .elder-input__element {
      padding: 0;
    }
    .select-checklist__items {
      padding: 1.1em;
      max-height: 300px;
      overflow-y: auto;

      @include scrollShadow();
    }
  }
}
</style>
